import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Sparkles, GraduationCap, Users, Award, BookOpen, Heart, ShieldCheck,
  Rocket, ChevronRight, Quote, Trophy, Globe2, Baby, Calendar, MapPin, PartyPopper,
  Star, CheckCircle2, Play, Camera, TrendingUp, Lightbulb, Zap, Target, Medal, Sun
} from "lucide-react";
import { api } from "@/lib/api";
import { ASSETS, SCHOOL_INFO } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import Countdown from "@/components/Countdown";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const VALUES = [
  { icon: ShieldCheck, title: "Discipline", text: "Rigueur, ponctualité et respect mutuel au cœur du quotidien.", color: "#1E3A8A" },
  { icon: Award, title: "Excellence", text: "La réussite scolaire et l'épanouissement de chaque élève.", color: "#166534" },
  { icon: Rocket, title: "Innovation", text: "Pédagogie moderne et accompagnement personnalisé.", color: "#EA580C" },
  { icon: Heart, title: "Responsabilité", text: "Citoyens engagés, respectueux et responsables.", color: "#FBBF24" },
];

const CYCLES = [
  { icon: Baby, name: "Maternelle", desc: "3 — 5 ans · Éveil & socialisation", accent: "#FBBF24" },
  { icon: BookOpen, name: "Primaire", desc: "6 — 11 ans · Savoirs fondamentaux", accent: "#166534" },
  { icon: GraduationCap, name: "Collège", desc: "12 — 15 ans · BEPC en ligne de mire", accent: "#1E3A8A" },
  { icon: Trophy, name: "Lycée", desc: "16 — 19 ans · Préparation au BAC", accent: "#EA580C" },
];

const ACHIEVEMENTS = [
  { icon: Medal, title: "96%", subtitle: "Taux de réussite", note: "BEPC & BAC 2025" },
  { icon: Star, title: "Top 5", subtitle: "Établissement", note: "Région de Conakry" },
  { icon: Award, title: "100%", subtitle: "Enseignants", note: "Qualifiés & formés" },
  { icon: Heart, title: "4 800", subtitle: "Familles", note: "Nous font confiance" },
];

const TESTIMONIALS = [
  { name: "Mme Bah", role: "Maman de Aminata · CM1", text: "Mon enfant a progressé incroyablement en 6 mois. Suivi numérique impeccable.", avatar: "MB", color: "#EA580C" },
  { name: "M. Camara", role: "Papa de Ousmane · 6ème", text: "Payer les frais et suivre les notes en ligne — un confort énorme.", avatar: "MC", color: "#1E3A8A" },
  { name: "Mme Sylla", role: "Maman de Mariama · Maternelle", text: "Encadrement de qualité, infrastructures modernes, communication exemplaire.", avatar: "MS", color: "#166534" },
];

const DAY_TIMELINE = [
  { time: "07h45", title: "Accueil & rituel du matin", icon: Sun, color: "#FBBF24" },
  { time: "08h00", title: "Cours académiques", icon: BookOpen, color: "#166534" },
  { time: "10h00", title: "Récréation & goûter", icon: Heart, color: "#EA580C" },
  { time: "10h30", title: "Activités créatives", icon: Lightbulb, color: "#1E3A8A" },
  { time: "12h30", title: "Déjeuner équilibré", icon: Star, color: "#166534" },
  { time: "14h00", title: "Cours de l'après-midi", icon: Target, color: "#1E3A8A" },
  { time: "16h00", title: "Sport & parascolaires", icon: Zap, color: "#EA580C" },
  { time: "17h30", title: "Retour à la maison", icon: CheckCircle2, color: "#166534" },
];

function AnimatedCounter({ end, suffix = "" }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const duration = 1800;
    const steps = 60;
    const inc = end / steps;
    let cur = 0;
    const t = setInterval(() => {
      cur += inc;
      if (cur >= end) { setCount(end); clearInterval(t); } else setCount(Math.floor(cur));
    }, duration / steps);
    return () => clearInterval(t);
  }, [end]);
  return <span>{count.toLocaleString("fr-FR")}{suffix}</span>;
}

export default function Home() {
  const [stats, setStats] = useState({ students: 0, classes: 0, teachers: 0, success_rate: 0 });
  const [news, setNews] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [media, setMedia] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 100]);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/public/announcements?limit=3").then(({ data }) => setNews(data)).catch(() => {});
    api.get("/public/next-event").then(({ data }) => setNextEvent(data)).catch(() => {});
    api.get("/site-content").then(({ data }) => setMedia(data)).catch(() => {});
  }, []);

  // Contenus publiés (Alimentation du site) — les « À la une » passent devant
  const byFeatured = (a, b) => (b.is_featured === true) - (a.is_featured === true);
  const photosM = media.filter(m => m.type === "gallery" && m.media_url).sort(byFeatured);
  const videosM = media.filter(m => m.type === "video" && m.media_url).sort(byFeatured);
  const teaser = [...photosM, ...videosM].sort(byFeatured).slice(0, 4);

  return (
    <div data-testid="page-home" className="overflow-x-hidden">

      {/* ============== HERO — Cinematic asymmetric ============== */}
      <section className="relative min-h-[92vh] bg-[#F8FAFC] overflow-hidden">
        {/* Decorative gradient blobs */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#166534]/15 blur-3xl rounded-full -translate-y-1/4 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#EA580C]/15 blur-3xl rounded-full translate-y-1/4 -translate-x-1/4 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7 z-10">
              {/* Ratings & trust */}
              <div className="flex items-center gap-4 mb-7 flex-wrap">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#166534] to-[#1E3A8A] flex items-center justify-center">
                    <Users className="h-3.5 w-3.5 text-white" strokeWidth={2.2} />
                  </div>
                  <span className="text-xs font-semibold text-slate-700">4 800+ familles</span>
                </div>
                <div className="flex items-center gap-1 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className="h-3.5 w-3.5 fill-[#FBBF24] text-[#FBBF24]" />)}
                  </div>
                  <span className="text-xs font-semibold text-slate-700 ml-1">4.9/5 sur Facebook</span>
                </div>
              </div>

              <h1 className="font-heading font-extrabold leading-[0.95] tracking-[-0.04em] text-[#0F172A]">
                <span className="block text-4xl sm:text-5xl lg:text-[64px] text-[#1E3A8A]">L&apos;école qui</span>
                <span className="block text-5xl sm:text-6xl lg:text-[80px] mt-2">
                  <span className="relative inline-block">
                    <span className="relative z-10 italic font-light text-[#166534]" style={{ fontFamily: "Bricolage Grotesque, serif" }}>révèle</span>
                    <span className="absolute bottom-2 left-0 right-0 h-3 bg-[#FBBF24]/40 -z-0 rounded" />
                  </span>{" "}
                  <span className="text-[#EA580C]">le potentiel</span>
                </span>
                <span className="block text-3xl sm:text-4xl lg:text-5xl mt-3 text-slate-700">de chaque enfant guinéen.</span>
              </h1>

              <p className="mt-7 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                De la maternelle au baccalauréat, <strong className="text-[#1E3A8A]">Les Écoles Mazid</strong> incarnent une éducation moderne, rigoureuse et bienveillante — 100% connectée à votre quotidien.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/inscription">
                  <Button size="lg" className="bg-[#166534] hover:bg-[#14532d] text-white rounded-full px-7 py-6 text-sm font-bold gap-2 shadow-lg shadow-[#166534]/20" data-testid="hero-register-btn">
                    Inscrire un enfant <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button size="lg" variant="outline" className="border-2 border-[#1E3A8A]/30 text-[#1E3A8A] hover:bg-blue-50 rounded-full px-7 py-6 text-sm font-bold gap-2" data-testid="hero-demo-btn">
                    <Sparkles className="h-4 w-4" /> Visite guidée
                  </Button>
                </Link>
                <Link to="/galerie">
                  <Button size="lg" variant="outline" className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 rounded-full px-7 py-6 text-sm font-bold gap-2" data-testid="hero-gallery-btn">
                    <Play className="h-4 w-4 fill-current" /> Voir la galerie
                  </Button>
                </Link>
              </div>

              {/* Quick contact */}
              <div className="mt-8 flex items-center gap-3 text-sm text-slate-600">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span>Inscriptions <strong className="text-[#1E3A8A]">ouvertes</strong> pour 2026-2027 ·</span>
                <a href={`tel:${SCHOOL_INFO.phones[0].replace(/\s/g, "")}`} className="font-semibold text-[#EA580C] hover:underline">{SCHOOL_INFO.phones[0]}</a>
              </div>
            </motion.div>

            {/* Right collage — vrais visuels uniquement (bâtiment + logo + vidéo) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ y: heroY }}
              className="lg:col-span-5 relative h-[520px] hidden lg:block"
            >
              {/* Grande photo — 1er visuel publié (À la une en priorité) */}
              {photosM[0] && (
                <div className="absolute top-0 left-0 w-[320px] h-[400px] rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white">
                  <img src={photosM[0].media_url} alt={photosM[0].title} className="w-full h-full object-cover" />
                </div>
              )}
              {/* Deuxième photo publiée */}
              {photosM[1] && (
                <div className="absolute top-10 right-0 w-[220px] h-[220px] rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white">
                  <img src={photosM[1].media_url} alt={photosM[1].title} className="w-full h-full object-cover" />
                </div>
              )}
              {/* Vidéo publiée en aperçu */}
              {videosM[0] && (
                <div className="absolute bottom-8 right-4 w-[260px] h-[180px] rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white bg-black">
                  <video src={videosM[0].media_url} muted autoPlay loop playsInline className="w-full h-full object-cover" />
                </div>
              )}
              {/* Floating stat card */}
              <div className="absolute top-[260px] right-[240px] bg-white rounded-2xl shadow-2xl p-4 ring-1 ring-slate-200 backdrop-blur z-10">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FBBF24] to-[#EA580C] flex items-center justify-center">
                    <Trophy className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="font-heading font-extrabold text-2xl text-[#1E3A8A] leading-none">96%</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-1">Réussite 2025</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============== TICKER / Marquee — partners style ============== */}
      <section className="bg-[#1E3A8A] text-white py-4 overflow-hidden border-y-4 border-[#FBBF24]">
        <div className="flex animate-[scroll_30s_linear_infinite] gap-12 whitespace-nowrap">
          {Array.from({ length: 3 }).map((_, k) => (
            <div key={k} className="flex gap-12 items-center text-sm font-bold uppercase tracking-[0.18em]">
              <span className="text-[#FBBF24]">✦ Faisons mieux</span>
              <span>Maternelle</span>
              <span className="text-[#FBBF24]">✦</span>
              <span>Primaire</span>
              <span className="text-[#FBBF24]">✦</span>
              <span>Collège</span>
              <span className="text-[#FBBF24]">✦</span>
              <span>Lycée</span>
              <span className="text-[#FBBF24]">✦</span>
              <span>Mazid AI Connect</span>
              <span className="text-[#FBBF24]">✦</span>
            </div>
          ))}
        </div>
        <style>{`@keyframes scroll { from { transform: translateX(0); } to { transform: translateX(-33.33%); } }`}</style>
      </section>

      {/* ============== ACHIEVEMENTS — Big numbers bento ============== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-14">
            <div className="chip mb-4 mx-auto inline-flex"><Medal className="h-3 w-3" /><span>Nos accomplissements</span></div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1E3A8A] tracking-[-0.03em]">
              Les chiffres qui <span className="text-[#EA580C]">parlent</span>.
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {ACHIEVEMENTS.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative bg-[#F8FAFC] border border-slate-200 rounded-3xl p-7 hover:shadow-xl transition-all hover:-translate-y-1 group"
                data-testid={`achievement-${i}`}
              >
                <a.icon className="h-7 w-7 text-[#EA580C] mb-6" strokeWidth={1.5} />
                <div className="font-heading font-extrabold text-5xl lg:text-6xl text-[#1E3A8A] tracking-tight">{a.title}</div>
                <div className="mt-2 font-semibold text-slate-800">{a.subtitle}</div>
                <div className="text-xs text-slate-500 mt-1">{a.note}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== OPEN HOUSE COUNTDOWN ============== */}
      {nextEvent && nextEvent.event_date && (
        <section className="relative py-24 overflow-hidden bg-gradient-to-br from-[#EA580C] via-[#FB923C] to-[#FBBF24] text-white" data-testid="section-open-house">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white blur-3xl" />
            <div className="absolute bottom-10 right-20 w-64 h-64 rounded-full bg-[#1E3A8A] blur-3xl" />
          </div>
          <div className="max-w-7xl mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/25 mb-5 backdrop-blur">
                <PartyPopper className="h-4 w-4 text-[#FBBF24]" />
                <span className="text-xs font-bold uppercase tracking-[0.18em]">Prochain événement</span>
              </div>
              <h2 className="mazid-headline text-4xl sm:text-5xl lg:text-6xl mb-5 leading-[1.05]">{nextEvent.title}</h2>
              <p className="text-base sm:text-lg text-white/95 leading-relaxed mb-6 max-w-2xl">{nextEvent.content}</p>
              <div className="space-y-3 mb-7 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0"><Calendar className="h-4 w-4" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-wider opacity-80 font-bold">Date</div>
                    <div className="font-semibold">{new Date(nextEvent.event_date).toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
                  </div>
                </div>
                {nextEvent.event_location && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0"><MapPin className="h-4 w-4" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-wider opacity-80 font-bold">Lieu</div>
                      <div className="font-semibold">{nextEvent.event_location}</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-[#EA580C] hover:bg-slate-100 rounded-full px-7 py-6 font-bold gap-2" data-testid="event-reserve-btn">
                    Réserver ma place <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <WhatsAppButton message={`Bonjour, je souhaite confirmer ma participation à : ${nextEvent.title}`} label="Confirmer par WhatsApp" testId="event-whatsapp-btn" className="!bg-white !text-[#25D366] hover:!bg-slate-100 px-7 py-3 !rounded-full" />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-white/15 backdrop-blur-xl border border-white/30 rounded-3xl p-6 sm:p-8 shadow-2xl">
                <div className="text-center mb-5 text-white">
                  <div className="text-xs uppercase tracking-[0.25em] font-bold mb-1 drop-shadow">Plus que</div>
                  <div className="font-heading text-2xl font-extrabold drop-shadow-lg">avant le grand jour</div>
                </div>
                <Countdown targetIso={nextEvent.event_date} variant="dark" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============== CYCLES — Premium asymmetric cards ============== */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="chip mb-4"><GraduationCap className="h-3 w-3" /><span>Parcours d&apos;excellence</span></div>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1E3A8A] tracking-[-0.03em] leading-tight">
                Un cycle pour <span className="text-[#166534]">chaque âge</span>,<br />
                une attention <span className="text-[#EA580C]">unique</span>.
              </h2>
            </div>
            <Link to="/cycles" className="text-[#166534] font-bold flex items-center gap-1 hover:gap-2 transition-all">
              Découvrir les programmes <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {CYCLES.map((c, i) => (
              <Link key={i} to="/cycles" className="group relative aspect-[3/4] rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 duration-300" data-testid={`home-cycle-${i}`}>
                {/* Background gradient illustration (no stock photo) */}
                <div className="absolute inset-0" style={{ background: `linear-gradient(155deg, ${c.accent} 0%, ${c.accent}cc 45%, #0F172A 110%)` }} />
                <div className="absolute inset-0 opacity-25 bg-dot-pattern" />
                <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-40" style={{ backgroundColor: c.accent }} />

                <div className="absolute top-4 right-4 w-10 h-10 rounded-full backdrop-blur-md bg-white/20 flex items-center justify-center border border-white/30">
                  <c.icon className="h-4 w-4 text-white" />
                </div>

                {/* Giant icon as illustration */}
                <c.icon className="absolute top-12 left-6 h-28 w-28 text-white/15" strokeWidth={1.2} />

                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <div className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tight mb-1">{c.name}</div>
                  <div className="text-sm opacity-95 mb-4">{c.desc}</div>
                  <div className="inline-flex items-center gap-1 text-sm font-bold group-hover:gap-2 transition-all">
                    Découvrir <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 h-1 group-hover:h-2 transition-all w-full bg-white/70" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============== A DAY AT MAZID — Timeline ============== */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-14">
            <div className="chip mb-4 mx-auto inline-flex"><Sun className="h-3 w-3" /><span>Une journée à Mazid</span></div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1E3A8A] tracking-[-0.03em] mb-3">
              Du matin au soir, <span className="text-[#166534]">l&apos;épanouissement</span>.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Découvrez le rythme quotidien qui équilibre apprentissage, créativité et détente.</p>
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-[#FBBF24] via-[#166534] to-[#EA580C]" />
            <div className="grid grid-cols-2 lg:grid-cols-8 gap-4">
              {DAY_TIMELINE.map((d, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="text-center"
                  data-testid={`day-step-${i}`}
                >
                  <div className="relative mx-auto w-16 h-16 mb-4">
                    <div className="absolute inset-0 rounded-full" style={{ backgroundColor: d.color, opacity: 0.15 }} />
                    <div className="absolute inset-2 rounded-full flex items-center justify-center" style={{ backgroundColor: d.color }}>
                      <d.icon className="h-5 w-5 text-white" strokeWidth={2} />
                    </div>
                  </div>
                  <div className="font-mono font-bold text-xs text-slate-500">{d.time}</div>
                  <div className="font-semibold text-sm text-[#0F172A] mt-1">{d.title}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== GALLERY TEASER ============== */}
      <section className="py-24 bg-[#0F172A] text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-4 inline-flex"><Camera className="h-3 w-3" /><span>Galerie</span></div>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] leading-tight">
                La vie à Mazid,<br />en <span className="text-[#FBBF24]">images</span>.
              </h2>
            </div>
            <Link to="/galerie" className="text-[#FBBF24] font-bold flex items-center gap-1 hover:gap-2 transition-all">
              Voir toute la galerie <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          {teaser.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {teaser.map((item) => (
                <Link key={item.id} to="/galerie" className="group relative aspect-square rounded-2xl overflow-hidden bg-black" data-testid={`home-teaser-${item.id}`}>
                  {item.type === "video" ? (
                    <video src={item.media_url} muted autoPlay loop playsInline className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <img src={item.media_url} alt={item.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  )}
                  <div className="absolute inset-0 bg-black/25 group-hover:bg-black/45 transition-colors" />
                  {item.is_featured ? (
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-[#FBBF24] text-[#0F172A] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"><Star className="h-2.5 w-2.5 fill-current" /> À la une</div>
                  ) : item.type === "video" ? (
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-white/90 text-[#0F172A] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"><Play className="h-2.5 w-2.5 fill-current" /> Vidéo</div>
                  ) : null}
                  <div className="absolute bottom-3 left-3 right-3 text-white text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition line-clamp-2">{item.title}</div>
                  <Camera className="absolute top-3 right-3 h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/20 p-8 text-center text-slate-400 text-sm">Aucun contenu publié pour le moment.</div>
          )}
        </div>
      </section>

      {/* ============== FOUNDER QUOTE + VIDEO ============== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl aspect-video">
              <img src={ASSETS.founder} alt="Le fondateur des Écoles Mazid" className="w-full h-full object-cover" data-testid="founder-photo" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#FBBF24] text-[#1E3A8A] p-5 rounded-2xl shadow-xl max-w-[200px]">
              <div className="font-heading font-extrabold text-xl leading-tight">&quot;Faisons mieux&quot;</div>
              <div className="text-xs mt-2 font-semibold opacity-80">La devise du fondateur</div>
            </div>
          </div>
          <div>
            <Quote className="h-12 w-12 text-[#FBBF24] mb-5" strokeWidth={1.5} />
            <blockquote className="font-heading text-3xl sm:text-4xl font-bold text-[#1E3A8A] leading-tight tracking-tight mb-7">
              &quot;Construire l&apos;école que nos enfants méritent, où chaque potentiel est <span className="text-[#166534] italic" style={{ fontFamily: "Bricolage Grotesque, serif", fontWeight: 300 }}>cultivé</span>.&quot;
            </blockquote>
            <div className="flex items-center gap-4 p-5 bg-[#F8FAFC] rounded-2xl border border-slate-200">
              <img src={ASSETS.founder} alt="Fondateur" className="w-16 h-16 rounded-2xl object-cover ring-2 ring-[#166534]/30" />
              <div>
                <div className="font-heading font-bold text-[#1E3A8A]">Le Fondateur</div>
                <div className="text-xs text-slate-500">Directeur Général · Les Écoles Mazid</div>
              </div>
              <Link to="/a-propos" className="ml-auto text-[#166534] text-sm font-semibold hover:underline">Lire la suite →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============== VALUES ============== */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-14">
            <div className="chip mb-4 mx-auto inline-flex"><Heart className="h-3 w-3" /><span>Nos valeurs</span></div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1E3A8A] tracking-[-0.03em]">
              Ce qui nous guide <span className="text-[#166534]">chaque jour</span>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <div key={i} className="group relative bg-white rounded-3xl p-7 border border-slate-200 hover:-translate-y-1 hover:shadow-xl transition-all" data-testid={`value-card-${i}`}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${v.color}15`, color: v.color }}>
                  <v.icon className="h-7 w-7" strokeWidth={1.6} />
                </div>
                <h3 className="font-heading font-bold text-2xl mb-2 text-[#0F172A]">{v.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{v.text}</p>
                <div className="absolute top-7 right-7 font-heading font-extrabold text-3xl opacity-10" style={{ color: v.color }}>0{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TESTIMONIALS — Cards with avatars ============== */}
      <section className="py-24 bg-gradient-to-br from-[#1E3A8A] via-[#166534] to-[#166534] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 bg-dot-pattern" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <div className="text-center mb-14">
            <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-4 inline-flex"><Star className="h-3 w-3 fill-current" /><span>Ils nous font confiance</span></div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em]">
              <span className="text-[#FBBF24]">4 800+</span> familles<br />une seule promesse.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-7 backdrop-blur hover:bg-white/10 transition-colors" data-testid={`testimonial-${i}`}>
                <div className="flex mb-4">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="h-4 w-4 fill-[#FBBF24] text-[#FBBF24]" />)}
                </div>
                <p className="text-sm leading-relaxed text-slate-100 mb-6">&quot;{t.text}&quot;</p>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: t.color }}>
                    {t.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-slate-300">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="relative bg-gradient-to-br from-[#166534] to-[#1E3A8A] rounded-3xl p-10 lg:p-16 text-white overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#FBBF24]/15 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#EA580C]/15 rounded-full blur-3xl" />
            <div className="relative text-center">
              <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-5 inline-flex"><Sparkles className="h-3 w-3" /><span>Inscriptions 2026-2027 ouvertes</span></div>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-5 tracking-[-0.03em] leading-tight">
                Offrez à votre enfant<br />
                <span className="text-[#FBBF24]">l&apos;avenir qu&apos;il mérite</span>.
              </h2>
              <p className="text-base sm:text-lg text-white/90 mb-9 max-w-2xl mx-auto">Inscription en ligne en 10 minutes. Notre équipe vous accompagne à chaque étape.</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/inscription">
                  <Button size="lg" className="bg-white text-[#166534] hover:bg-slate-100 rounded-full px-8 py-6 font-bold gap-2" data-testid="cta-register-btn">
                    Inscrire un enfant <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-2 border-white/40 bg-white/5 hover:bg-white/15 text-white rounded-full px-8 py-6 font-bold" data-testid="cta-contact-btn">
                    Prendre rendez-vous
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
